import { useContext, useEffect, useState } from "react";

import Checkbox from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";
import { updateAdmin } from "../../../helpers/Administradores/Administradores";
import { AdminContext } from "../../../context/AdminContext/AdminContext";
import { useMutation } from "@apollo/client";
import { UPDATE_USERS_PERMISSIONS,GET_PERMISSIONS_USER, GET_PERMISSIONS_USER_PANEL } from "../../../Querys/querys";
import AdministratorsContext from "../../../context/AdministratorsContext/AdministratorsContext";
import { getParentPermissionName, PERMISO_ACCESO_ADMIN } from "../../../helpers/Administradores/permissionCascade";
import { AttentionAlert2 } from "../../../alerts/attentionAlert2";

const BpCheckbox = (props: any) => {
  const { state, dispatch } = useContext(AdministratorsContext);
  const { adminState } = useContext(AdminContext);
  // Valor resuelto: props.Value puede venir como thunk (() => handleConfirmPermission(...)) o booleano.
  const resolved =
    typeof props.Value === "function" ? props.Value() : props.Value;
  const [value, setValue] = useState(Boolean(resolved));

  // Reactividad: reflejar el valor fresco tras el refetch de permisos (DBSB-458).
  useEffect(() => {
    setValue(Boolean(resolved));
  }, [resolved]);

  // Deshabilitar el hijo cuando su padre inmediato (nombre sin el último segmento)
  // está apagado. Genérico para todos los módulos (DBSB-458). Fail-open si el padre
  // no está en la lista (no bloquea de más).
  const parentName = getParentPermissionName(props.NombreCampo);
  const permisosLista: any[] = Array.isArray(state?.Permisos)
    ? state.Permisos
    : [];
  const parentPerm = parentName
    ? permisosLista.find((p: any) => p?.name === parentName)
    : undefined;
  const [isSaving, setIsSaving] = useState(false);
  const isDisabled =
    Boolean(props.disabled) || isSaving || (parentPerm ? !parentPerm.status : false);

  const [updatePermission] = useMutation(UPDATE_USERS_PERMISSIONS, {
    refetchQueries: [
      { query: GET_PERMISSIONS_USER, variables:{getUsersId: state?._id}},
      { query: GET_PERMISSIONS_USER_PANEL, variables:{getUsersId: state?._id}},
    ],
  });

  const handleChange = async (event: any) => {
    if (isSaving) return;
    const checked = event.target.checked;

    // Salvaguarda anti-autobloqueo (DBSB-458): no permitir quitarte a ti mismo el
    // acceso al panel de administración (perderías esta pantalla y no podrías revertir).
    const editandoPropiaCuenta =
      adminState?.id != null && String(state?._id) === String(adminState.id);
    if (!checked && editandoPropiaCuenta && props.NombreCampo === PERMISO_ACCESO_ADMIN) {
      AttentionAlert2({
        text:
          "No puedes quitarte tu propio acceso al panel de administración. Pídeselo a otro administrador o hazlo desde otra cuenta.",
      });
      return;
    }

    setValue(checked);
    setIsSaving(true);
    try {
      await updatePermission({
        variables: {
          id: state?._id,
          input: {
            name: props.NombreCampo,
            status: checked,
          },
        },
      });
    } finally {
      setIsSaving(false);
    }
  };

  const BpIcon = styled("span")(({ theme }) => ({
    borderRadius: 3,
    width: 14,
    height: 14,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 0 0 1px rgb(16 22 26 / 40%)"
        : "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
    backgroundColor: theme.palette.mode === "dark" ? "#394b59" : "#ffffff",
    backgroundImage:
      theme.palette.mode === "dark"
        ? "linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))"
        : "linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))",
    ".Mui-focusVisible &": {
      outline: "2px auto rgba(19,124,189,.6)",
      outlineOffset: 2,
    },
    "input:disabled ~ &": {
      boxShadow: "none",
      background:
        theme.palette.mode === "dark"
          ? "rgba(57,75,89,.5)"
          : "rgba(206,217,224,.5)",
    },
  }));

  const BpCheckedIcon = styled(BpIcon)({
    backgroundColor: "#fabb00",
    backgroundImage:
      "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
    "&:before": {
      display: "block",
      width: 14,
      height: 14,
    },
    "input:hover ~ &": {
      backgroundColor: "#fabb00",
      width: 14,
      height: 14,
    },
  });

  return (
    <Checkbox
      sx={{
        "&:hover": { bgcolor: "transparent" },
      }}
      disableRipple
      color="default"
      checked={value}
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      inputProps={{ "aria-label": "Checkbox demo" }}
      onChange={handleChange}
      {...props}
      disabled={isDisabled}
    />
  );
};

export default BpCheckbox;
